/**
 * Album label resolution — frontend mirror of /app/backend/album_title.py.
 *
 * Product decision (2026-07-30, Msg §1–§7):
 *   * `album_title`    — official catalog identity (variant.name), always
 *                        populated. Primary line on every album card.
 *   * `album_subtitle` — collector's personal nickname, but ONLY when it is
 *                        a meaningful, non-generic string that differs from
 *                        the title. Returns `null` otherwise so callers can
 *                        skip the subtitle row entirely (no reserved space).
 *
 * Keep this list in lock-step with the backend's GENERIC_NICKNAMES set.
 */

const GENERIC_NICKNAMES = new Set([
  "album",
  "my album",
  "demo album",
  "new album",
  "untitled",
  "untitled album",
  "test",
  "testing",
  "test album",
  "collection",
  "my collection",
]);

const norm = (s) => (s || "").trim().toLowerCase();

/** Primary label — always returns a non-empty string. */
export function resolveAlbumTitle(variant, release) {
  const v = variant?.name;
  if (v && String(v).trim()) return String(v).trim();
  const r = release?.official_title || release?.name;
  if (r && String(r).trim()) return String(r).trim();
  return "Untitled album";
}

/**
 * Secondary italic label — returns `null` when the nickname is empty,
 * generic, or an exact case-insensitive echo of the album title. Callers
 * MUST render the subtitle row only when this is truthy.
 */
export function resolveAlbumSubtitle(nickname, albumTitle) {
  const n = (nickname || "").trim();
  if (!n) return null;
  const key = norm(n);
  if (GENERIC_NICKNAMES.has(key)) return null;
  if (key === norm(albumTitle)) return null;
  return n;
}

/**
 * Convenience — returns { title, subtitle } for a full album/catalog row.
 * Prefers backend-supplied `album_title` / `album_subtitle` when present
 * (so we stay consistent even if the client-side allow-list drifts).
 */
export function resolveAlbumLabels(album, catalog) {
  const backendTitle = album?.album_title;
  const backendSubtitle = album?.album_subtitle;
  if (backendTitle) {
    return { title: backendTitle, subtitle: backendSubtitle ?? null };
  }
  const title = resolveAlbumTitle(catalog?.album_variant, catalog?.release);
  const subtitle = resolveAlbumSubtitle(album?.nickname, title);
  return { title, subtitle };
}
